import { Injectable } from '@angular/core';
import { HttpService, TenantModel } from '@gooru/navigator-core-widget-ui';
import { AuthService } from '@gooru/navigator-auth-widget-ui';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LOGINTYPE } from '../../constants/helper-constant';

@Injectable({
  providedIn: 'root',
})
export class OtpService {
  private readonly ANONYMOUS_TOKEN = 'anonymous_token';
  private readonly LOGGED_IN = 'logged_in';
  private readonly smsService = 'api/v1/sms-service';

  constructor(
    private authService: AuthService,
    private httpService: HttpService
  ) {}

  /**
   * @function sendOtp
   * validateForm method used to send OTP
   */
  public async sendOtp(
    input: { phone?: string; email?: string },
    forceOtp: boolean = false
  ) {
    const session = await this.authService.signInAnonymous(
      environment.clientId,
      environment.clientKey
    );
    sessionStorage.setItem(this.ANONYMOUS_TOKEN, session.access_token);
    const headers = this.httpService.getHeaders(session.access_token);
    const reqOpts = { headers };

    const endpoint = `${this.smsService}/generate/otp`;
    const url = this.httpService.formURL(endpoint);
    const payload: { phoneNumber?: string; email?: string } = {};

    if (input.phone) {
      payload.phoneNumber = input.phone;
    } else if (input.email) {
      payload.email = input.email;
    }
    if (input.email && !forceOtp) {
      const email = input.email;
      return this.authService
        .fetchUserAccounts(email, reqOpts)
        .then(async (userData) => {
          if (userData?.length !== 0) {
            if (userData.length === 1 && userData[0].login_type === LOGINTYPE.SERVER2SERVER) {
              const response = await firstValueFrom(this.httpService.post(url, payload, reqOpts));
              return { response, newUser: false, accounts: userData };
            } else {
              return { response: userData, newUser: false, accounts: userData  };
            }
          } else {
            const response = await firstValueFrom(this.httpService.post(url, payload, reqOpts));
            return { response, newUser: true,accounts: userData  };
          }
        })
        .catch(async (err) => {
          const response = await firstValueFrom(this.httpService.post(url, payload, reqOpts));
          return { response, newUser: true, accounts: null  };
        });
    }
    const response = await firstValueFrom(this.httpService.post(url, payload, reqOpts));
    return { response, newUser: true, accounts: null  };
  }

  /**
   * @function checkUsernameIsValid
   * check username is valid
   */
  public async checkUsernameIsValid(username: string) {
    const session = await this.authService.signInAnonymous(
      environment.clientId,
      environment.clientKey
    );
    const headers = this.httpService.getHeaders(session.access_token);
    const reqOpts = { headers };
    return this.authService.fetchUserAccounts(username, reqOpts)
      .then((userData) => {
        if (userData?.length !== 0 && userData[0].login_type === LOGINTYPE.CREDENTIAL) {
          return true;
        }
        return false;
      })
      .catch(async (err) => {
        return false;
      });
  }

  /**
   * @function verifyOtp
   * validateForm method used to verify OTP
   */
  public async verifyOtp(input: {
    phoneNumber?: string;
    email?: string;
    otp: string;
  }) {
    const accessToken = sessionStorage.getItem(this.ANONYMOUS_TOKEN);
    const headers = this.httpService.getHeaders(accessToken as string);
    const reqOpts = { headers };
    const endpoint = `${this.smsService}/verify/otp`;
    const url = this.httpService.formURL(endpoint);

    const payload: {
      phoneNumber?: string;
      email?: string;
      otp: string;
    } = {
      otp: input.otp,
    };

    if (input.phoneNumber) {
      payload.phoneNumber = input.phoneNumber;
    } else if (input.email) {
      payload.email = input.email;
    }

    return firstValueFrom(this.httpService.post(url, payload, reqOpts));
  }

  /**
   * @function loggedIn
   * validateForm method used to store loggedin status
   */
  public loggedIn() {
    localStorage.setItem(this.LOGGED_IN, '1');
  }

  /**
   * @function isLoggedIn
   * validateForm method used to get loggedin status
   */
  public isLoggedIn() {
    return localStorage.getItem(this.LOGGED_IN) !== null;
  }
}
